import React, { useState, useEffect, useMemo } from 'react';
import {
  ClipboardList,
  Filter,
  Download,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Loader2,
  TrendingUp,
  Package,
  MapPin,
  Warehouse
} from 'lucide-react';
import { reservationService } from '../../../../services/reservationService';
import { StcReservation, ReservationSummary } from '../../../../types/reservation';
import { useAuth } from '../../../../contexts/AuthContext';
import { BackButton } from '../../../common/BackButton';

// Helper function to format date for display
const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('no-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Helper function to format numbers with thousand separator
const formatNumber = (num: number): string => {
  return num.toLocaleString('no-NO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

type SortableColumn = keyof Pick<StcReservation, 'resDate' | 'warehouseID' | 'binCode' | 'itemNo' | 'resQuantity' | 'orderNoERP'>;
type SortDirection = 'asc' | 'desc';

interface ReservationsAppProps {
  onBack?: () => void;
}

export const ReservationsApp: React.FC<ReservationsAppProps> = ({ onBack }) => {
  const { user } = useAuth();

  // State management
  const [reservations, setReservations] = useState<StcReservation[]>([]);
  const [summary, setSummary] = useState<ReservationSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterPanelOpen, setFilterPanelOpen] = useState(true);

  // Filter state
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [itemNoFilter, setItemNoFilter] = useState('');
  const [binCodeFilter, setBinCodeFilter] = useState('');
  const [pListIdFilter, setPListIdFilter] = useState('');

  // Sorting state
  const [sortColumn, setSortColumn] = useState<SortableColumn>('resDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Load reservations and summary
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        warehouseId: warehouseFilter || undefined,
        itemNo: itemNoFilter || undefined,
        binCode: binCodeFilter || undefined,
        pListId: pListIdFilter ? parseInt(pListIdFilter) : undefined
      };

      const [reservationsData, summaryData] = await Promise.all([
        reservationService.getActiveReservations(filters),
        reservationService.getSummary()
      ]);

      setReservations(reservationsData);
      setSummary(summaryData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'En ukjent feil oppstod');
    } finally {
      setLoading(false);
    }
  };

  // Load on mount
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply filters
  const handleApplyFilters = () => {
    loadData();
  };

  // Reset filters
  const handleResetFilters = () => {
    setWarehouseFilter('');
    setItemNoFilter('');
    setBinCodeFilter('');
    setPListIdFilter('');
  };

  // Sorting logic
  const sortedReservations = useMemo(() => {
    const sorted = [...reservations].sort((a, b) => {
      let aValue: any = a[sortColumn];
      let bValue: any = b[sortColumn];

      // Handle null values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Convert to comparable values
      if (sortColumn === 'resDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [reservations, sortColumn, sortDirection]);

  // Calculate total quantity from filtered data
  const totalQuantity = useMemo(() => {
    return sortedReservations.reduce((sum, r) => sum + r.resQuantity, 0);
  }, [sortedReservations]);

  // Handle column sort
  const handleSort = (column: SortableColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Render sort indicator
  const renderSortIndicator = (column: SortableColumn) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="inline ml-1" size={14} />
    ) : (
      <ChevronDown className="inline ml-1" size={14} />
    );
  };

  // Export to CSV
  const exportToCSV = () => {
    if (sortedReservations.length === 0) {
      alert('Ingen data å eksportere');
      return;
    }

    const rows = sortedReservations.map(r => ({
      Lager: r.warehouseID,
      Lokasjon: r.binCode,
      Varenummer: r.itemNo,
      Varebeskrivelse: r.description1 || '',
      'Reservert antall': r.resQuantity,
      Enhet: r.itemUnitCode || '',
      'Res. dato': formatDateTime(r.resDate),
      'Ordre (ERP)': r.orderNoERP || '',
      'Linje (ERP)': r.orderLineERP || '',
      Kunde: r.orderName || '',
      'Plukkliste ID': r.pListID,
      'GR Shipment': r.grShipmentNo || ''
    }));

    const csv = [
      Object.keys(rows[0]).join(';'),
      ...rows.map(row => Object.values(row).map(val => `"${val}"`).join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `reservasjoner_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-6 max-w-full mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <BackButton onClick={onBack || (() => {})} />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <ClipboardList size={32} className="text-teal-600" />
                Reservasjoner
              </h1>
              <p className="text-gray-600 mt-1">
                Oversikt over aktive vareserverasjoner i lageret
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Oppdater
            </button>
            <button
              onClick={exportToCSV}
              disabled={loading || sortedReservations.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              <Download size={16} />
              Eksporter
            </button>
          </div>
        </div>

        {/* Summary Statistics */}
        {summary && (
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-700">Totalt antall reservasjoner</p>
                  <p className="text-2xl font-bold text-blue-900">{summary.totalReservations}</p>
                </div>
                <ClipboardList className="text-blue-600" size={32} />
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-700">Total reservert mengde</p>
                  <p className="text-2xl font-bold text-green-900">{formatNumber(summary.totalQuantityReserved)}</p>
                </div>
                <TrendingUp className="text-green-600" size={32} />
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-700">Unike varer</p>
                  <p className="text-2xl font-bold text-purple-900">{summary.uniqueItems}</p>
                </div>
                <Package className="text-purple-600" size={32} />
              </div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-700">Unike lokasjoner</p>
                  <p className="text-2xl font-bold text-orange-900">{summary.uniqueLocations}</p>
                </div>
                <MapPin className="text-orange-600" size={32} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filter Panel */}
      <div className="bg-white rounded-lg shadow-md mb-4 border border-gray-200">
        <button
          onClick={() => setFilterPanelOpen(!filterPanelOpen)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-teal-600" />
            <span className="font-semibold text-gray-900">Filtre</span>
          </div>
          {filterPanelOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>

        {filterPanelOpen && (
          <div className="p-4 border-t border-gray-200">
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Warehouse size={14} className="inline mr-1" />
                  Lager
                </label>
                <input
                  type="text"
                  value={warehouseFilter}
                  onChange={(e) => setWarehouseFilter(e.target.value)}
                  placeholder="Filtrer på lager..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Package size={14} className="inline mr-1" />
                  Varenummer
                </label>
                <input
                  type="text"
                  value={itemNoFilter}
                  onChange={(e) => setItemNoFilter(e.target.value)}
                  placeholder="Filtrer på varenummer..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin size={14} className="inline mr-1" />
                  Lokasjon
                </label>
                <input
                  type="text"
                  value={binCodeFilter}
                  onChange={(e) => setBinCodeFilter(e.target.value)}
                  placeholder="Filtrer på lokasjon..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <ClipboardList size={14} className="inline mr-1" />
                  Plukkliste ID
                </label>
                <input
                  type="text"
                  value={pListIdFilter}
                  onChange={(e) => setPListIdFilter(e.target.value)}
                  placeholder="Filtrer på plukkliste..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleApplyFilters}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                <Search size={16} />
                Søk
              </button>
              <button
                onClick={handleResetFilters}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <RefreshCw size={16} />
                Nullstill
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-2 text-red-800">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="animate-spin text-teal-600" size={48} />
        </div>
      )}

      {/* Results Table */}
      {!loading && sortedReservations.length > 0 && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    onClick={() => handleSort('warehouseID')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Lager {renderSortIndicator('warehouseID')}
                  </th>
                  <th
                    onClick={() => handleSort('binCode')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Lokasjon {renderSortIndicator('binCode')}
                  </th>
                  <th
                    onClick={() => handleSort('itemNo')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Varenummer {renderSortIndicator('itemNo')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Beskrivelse
                  </th>
                  <th
                    onClick={() => handleSort('resQuantity')}
                    className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Ant. reservert {renderSortIndicator('resQuantity')}
                  </th>
                  <th
                    onClick={() => handleSort('resDate')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Res. dato {renderSortIndicator('resDate')}
                  </th>
                  <th
                    onClick={() => handleSort('orderNoERP')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Ordre (ERP) {renderSortIndicator('orderNoERP')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Linje (ERP)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Kunde
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Plukkliste
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    GR Shipment
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedReservations.map((reservation, index) => (
                  <tr key={reservation.resID} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 text-sm text-gray-900">{reservation.warehouseID}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">{reservation.binCode}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">{reservation.itemNo}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{reservation.description1 || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">
                      {formatNumber(reservation.resQuantity)} {reservation.itemUnitCode || ''}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDateTime(reservation.resDate)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{reservation.orderNoERP || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{reservation.orderLineERP || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{reservation.orderName || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{reservation.pListID}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{reservation.grShipmentNo || '-'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-sm font-bold text-gray-900">
                    Totalt ({sortedReservations.length} reservasjoner)
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                    {formatNumber(totalQuantity)}
                  </td>
                  <td colSpan={6}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* No Results */}
      {!loading && sortedReservations.length === 0 && !error && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-12 text-center">
          <ClipboardList size={64} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Ingen reservasjoner funnet</h3>
          <p className="text-gray-600">
            Prøv å justere filtrene dine for å se andre resultater
          </p>
        </div>
      )}
    </div>
  );
};
